import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isTextClipped } from '@/frontend/features/Fde/investigations/format'

interface Props {
  text: string
  expandLabel: string
  collapseLabel: string
  preview?: 'brief' | 'standard'
  textClassName?: string
}

/** Visual line preview only: one complete text node stays in the document and
 * the durable journal is never shortened, rewritten or duplicated for display.
 */
export default function DisclosureText({ text, expandLabel, collapseLabel, preview = 'standard', textClassName }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [expandable, setExpandable] = useState(false)
  const paragraph = useRef<HTMLParagraphElement>(null)
  const id = useId()
  useEffect(() => {
    const element = paragraph.current
    if (!element || expanded) return
    const measure = () => setExpandable(isTextClipped(element.clientHeight, element.scrollHeight))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [text, expanded, preview])
  return <div className="grid min-w-0 gap-2">
    <p ref={paragraph} id={id} className={cn('whitespace-pre-line break-words text-sm leading-relaxed', textClassName,
      !expanded && (preview === 'brief' ? 'line-clamp-2' : 'line-clamp-3'))}>{text}</p>
    {expandable && <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-controls={id} className="flex w-fit items-center gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {expanded ? collapseLabel : expandLabel}<ChevronDown className={cn('size-3 transition-transform', expanded && 'rotate-180')} aria-hidden />
    </button>}
  </div>
}
